import {ShoppingCart, X} from 'lucide-react';
import {POSTHOG_UNMASK_CLASS} from '@common-web/lib/analytics';
import {useEnrollSteps} from '@client/pages/public/enroll/enrollStepsContext';
import {formatUsd, getPlan} from '@client/pages/public/enroll/enrollModel';
import {COVERAGE_TIER_LABELS, PROTECTION_CATEGORIES} from '@client/pages/public/enroll/types';
import {sitePayPeriod} from '@common/enrollmentSite/payPeriod';

export function CartRail() {
  const {group, getTier, selections, total, clearSelection} = useEnrollSteps();
  const payPeriod = sitePayPeriod(group);

  const lineItems = group.products
    .map((product) => {
      const selection = selections[product.category];
      // Chosen plan → priced line.
      if (selection?.planId) {
        const plan = getPlan(group, selection.planId);
        if (!plan) return null;
        const tier = getTier(product.category);
        return {
          category: product.category,
          productLabel: product.label,
          planName: plan.name,
          tierLabel: product.tierSelectable !== false ? COVERAGE_TIER_LABELS[tier] : null,
          price: plan.rates[tier],
          declined: false,
          declineWord: null,
        };
      }
      // Declined core coverage → $0 "Declined/Skipped" line (optional protection is omitted).
      if (selection?.declined && !PROTECTION_CATEGORIES.includes(product.category)) {
        return {
          category: product.category,
          productLabel: product.label,
          planName: null,
          tierLabel: null,
          price: 0,
          declined: true,
          declineWord: product.declineStyle === 'skipped' ? 'Skipped' : 'Declined',
        };
      }
      // Untouched, or a declined optional protection product → no cart line.
      return null;
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  return (
    <aside className={`rounded-xl border border-neutral-200 bg-white p-5 ${POSTHOG_UNMASK_CLASS}`}>
      <div className='flex items-center gap-2 text-sm font-semibold text-neutral-900'>
        <ShoppingCart className='h-4 w-4 text-[var(--enroll-primary)]' />
        Your selections
      </div>

      {lineItems.length === 0 ? (
        <p className='mt-4 text-sm text-neutral-500'>
          Nothing added yet. Choose a plan or decline each coverage to continue.
        </p>
      ) : (
        <ul className='mt-4 space-y-3'>
          {lineItems.map((item) =>
            item.declined ? (
              <li key={item.category} className='flex items-center justify-between gap-3 text-sm'>
                <p className='font-medium text-red-600'>
                  {item.productLabel} — {item.declineWord}
                </p>
                <button
                  type='button'
                  onClick={() => clearSelection(item.category)}
                  className='text-xs font-semibold text-[var(--enroll-accent)] underline-offset-4 hover:underline'
                >
                  undo
                </button>
              </li>
            ) : (
              <li key={item.category} className='flex items-start gap-3 text-sm'>
                <div className='flex-1'>
                  <p className='font-medium text-neutral-800'>{item.productLabel}</p>
                  <p className='text-xs text-neutral-500'>
                    {item.planName}
                    {item.tierLabel ? ` | ${item.tierLabel}` : ''}
                  </p>
                </div>
                <span className='whitespace-nowrap font-semibold text-neutral-900'>
                  {formatUsd(item.price)}
                </span>
                <button
                  type='button'
                  onClick={() => clearSelection(item.category)}
                  aria-label={`Remove ${item.productLabel}`}
                  className='mt-0.5 text-neutral-400 transition-colors hover:text-neutral-700'
                >
                  <X className='h-4 w-4' />
                </button>
              </li>
            ),
          )}
        </ul>
      )}

      <div className='mt-5 border-t border-neutral-200 pt-4'>
        <div className='flex items-center justify-between'>
          <span className='text-sm font-semibold text-neutral-700'>{payPeriod.total}</span>
          <span className='text-lg font-bold text-[var(--enroll-ink)]'>{formatUsd(total)}</span>
        </div>
      </div>
    </aside>
  );
}
